/**
 * Koble ansatte og lønnsarter (issue #110, owner 2026-10-16): each person to their employee in the
 * company's PowerOffice Go, and each pay type to Go's pay item, from the server's suggestions
 * (backend/services/powerofficeMatch.js). The administrator looks them over and saves; nothing is
 * written before Lagre. Opens by itself right after Koble til, so it is part of getting started.
 * Go's people nobody in Timefly is are listed below it to add (owner 2026-10-19: fetch the people from
 * PowerOffice): each is made through the same POST /admin/users as Ny ansatt, with no mail, then
 * given their Go number as Lønns-ID.
 */
import { errorText, get, post, put } from '@/api/client';
import { usePayrollTypes } from '@/api/payroll';
import { T } from '@/i18n';
import { Button, Chip, toast } from '@/ui';
import { Notice } from '@/ui/page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { POWEROFFICE, type PayrollSystem } from './payrollSystem';

type Basis = 'current' | 'email' | 'phone' | 'name_birth' | 'number' | 'name' | 'near' | 'taken' | null;
interface Row {
  suggestion: string | null;
  basis: Basis;
  sure: boolean;
  current: string | null;
  name: string;
}
/** GET /admin/poweroffice/match, and the same from /admin/tripletex/match (#187) */
export interface PowerOfficeMatch {
  client_name: string | null;
  employees: { number: string; name: string }[];
  /** percent: Go's PercentOfHourlyWage percentage; over 100 pays the base too (#183) */
  pay_items: { code: string; name: string; fixed: boolean; percent?: number }[];
  people: (Row & { user_id: number })[];
  /** 'regular', 'hoytid', 'avspasering', a work pay type's id or 'absence:<id>' */
  types: (Row & { key: number | string })[];
  /** the other stores on the same Go company (#183) */
  shared_stores?: { id: number; name: string }[];
  /** the Go client is shared with stores the caller does not run, and this store has no department: new people stay hidden */
  department_needed?: boolean;
  /** Go's people nobody in Timefly is or is suggested to be */
  missing?: Missing[];
}
export interface Missing {
  number: string;
  name: string;
  lastname: string;
  email: string | null;
  phone: string | null;
  birth_date: string | null;
  /** already a member of another store on the same PowerOffice company (#183): added, not made again */
  /** account: a Timefly login through another company, matched by email; that company is not named (owner 2026-10-19) */
  existing?: { user_id?: number; name?: string; stores: string[]; account?: boolean };
}
/**
 * The create fields a PowerOffice contract gives: pay, form, end date, agreed week. A temporary job
 * without an end date leaves the form out, since the create refuses it (#108).
 */
export function contractOf(c: Record<string, string | number> | undefined) {
  if (!c) return {};
  const out: Record<string, string | number> = {};
  for (const k of [
    'contract_type',
    'hourly_rate',
    'monthly_rate',
    'employment_start',
    'end_date',
    'agreed_week_hours',
  ] as const)
    if (c[k] != null) out[k] = c[k];
  if (c.employment_form && (c.employment_form !== 'midlertidig' || c.end_date))
    out.employment_form = c.employment_form;
  return out;
}
// the username Ny ansatt would be typed with: the address, else name and Go number
const userOf = (m: Missing) =>
  (m.email ?? `${m.name}.${m.lastname}.${m.number}`).toLowerCase().replace(/\s+/g, '');

/**
 * Adds the chosen people from Go, one by one so one refusal does not stop the rest, then links them:
 * Koble ansatte and the setup wizard (Oppsett, 2026-10-19) both add people this way.
 */
export async function addFromGo(chosen: Missing[], sys: PayrollSystem = POWEROFFICE) {
  const made: { user_id: number; number: string }[] = [];
  let joined = 0;
  const no: Record<string, string> = {};
  // pay and contract from PowerOffice go along (#183 phase 2); without them the person is made as before
  const picked = chosen.filter((x) => !x.existing);
  const terms = await post<{ contracts: Record<string, Record<string, string | number>> }>(
    `${sys.base}/contracts/lookup`,
    { numbers: picked.map((x) => x.number) },
  )
    .then((r) => r.contracts ?? {})
    .catch(() => ({}) as Record<string, Record<string, string | number>>);
  for (const m of chosen) {
    try {
      if (m.existing) {
        // the same person, now also in this store, with the Go number as Lønns-ID (#183)
        // a login through another company is found by the server from the payroll system's email: only the number goes
        await post(
          `${sys.base}/join`,
          m.existing.user_id ? { user_id: m.existing.user_id, number: m.number } : { number: m.number },
        );
        joined++;
        continue;
      }
      const u = await post<{ id: number }>('/admin/users', {
        username: userOf(m),
        name: m.name,
        lastname: m.lastname,
        email: m.email ?? '',
        phone: m.phone ?? '',
        birth_date: m.birth_date,
        employee_id: m.number,
        ...contractOf(terms[m.number]),
        send_welcome: false,
        // nobody gets mail here: the account waits for the welcome the administrator sends
        ...(m.email ? { password: crypto.randomUUID() } : {}),
      });
      made.push({ user_id: u.id, number: m.number });
    } catch (e) {
      no[m.number] = errorText(e);
    }
  }
  if (made.length) await put(`${sys.base}/match`, { people: made, types: [] });
  return { made: made.length + joined, no };
}

function AddFromGo({ missing, onDone, sys }: { missing: Missing[]; onDone: () => void; sys: PayrollSystem }) {
  const qc = useQueryClient();
  // someone already in another store needs no birth date here: the person exists
  const addable = missing.filter((m) => m.existing || (m.birth_date && m.name && m.lastname));
  const [pick, setPick] = useState<Set<string>>(() => new Set(addable.map((m) => m.number)));
  const [refused, setRefused] = useState<Record<string, string>>({});
  const add = useMutation({
    mutationFn: () =>
      addFromGo(
        addable.filter((x) => pick.has(x.number)),
        sys,
      ),
    onSuccess: ({ made, no }) => {
      setRefused(no);
      qc.invalidateQueries({ queryKey: ['users'] });
      if (made) {
        toast(
          T('{n} lagt til fra {system}. De får ingen e-post før du sender velkomsten.', {
            n: made,
            system: sys.short,
          }),
        );
        onDone();
      }
    },
    onError: (e) => toast(errorText(e)),
  });
  const n = addable.filter((m) => pick.has(m.number)).length;
  return (
    <div data-testid="po-add" style={{ display: 'grid', gap: 8 }}>
      <b style={{ fontSize: 14 }}>
        {T('I {system}, ikke i Timefly ({n})', { n: missing.length, system: sys.short })}
      </b>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
        {missing.map((m) => {
          const ok = addable.includes(m);
          return (
            <li key={m.number}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  disabled={!ok || add.isPending}
                  checked={ok && pick.has(m.number)}
                  onChange={(e) => {
                    const next = new Set(pick);
                    if (e.target.checked) next.add(m.number);
                    else next.delete(m.number);
                    setPick(next);
                  }}
                />
                <span>
                  {m.number}, {m.name} {m.lastname}
                </span>
                {m.existing && (
                  <small style={{ color: 'var(--ink3)' }} data-testid="po-add-existing">
                    {m.existing.account
                      ? T('har Timefly fra før, legges til her og får beskjed på e-post')
                      : T('finnes i {stores}, legges til her', { stores: m.existing.stores.join(', ') })}
                  </small>
                )}
                {!ok && (
                  <small style={{ color: 'var(--ink3)' }}>
                    {T('mangler navn eller fødselsdato i {system}', { system: sys.short })}
                  </small>
                )}
                {refused[m.number] && <small style={{ color: 'var(--dgr-i)' }}>{refused[m.number]}</small>}
              </label>
            </li>
          );
        })}
      </ul>
      <span>
        <Button onClick={() => add.mutate()} disabled={add.isPending || n === 0}>
          {add.isPending ? T('Legger til…') : T('Legg til {n} i Timefly', { n })}
        </Button>
      </span>
    </div>
  );
}

/** shown when new people from a shared Go client are hidden until the store's department is chosen */
export const DEPT_NEEDED =
  'PowerOffice-klienten deles med butikker du ikke er administrator for. Velg butikkens avdeling eller lokasjon under Innstillinger, Lønn, så ser du nye ansatte der.';
export const BASIS: Record<Exclude<Basis, null>, [string, 'ok' | 'warn' | 'neu']> = {
  current: ['Allerede koblet', 'ok'],
  email: ['Lik e-post', 'ok'],
  phone: ['Likt telefonnummer', 'ok'],
  name_birth: ['Navn og fødselsdato', 'ok'],
  number: ['Ansattnr og navn', 'ok'],
  name: ['Bare navn, sjekk', 'warn'],
  near: ['Lignende navn, sjekk', 'warn'],
  taken: ['Valgt for en annen', 'warn'],
};
const why = (b: Basis, pick: string, r: Row) =>
  pick !== (r.suggestion ?? r.current ?? '') ? (
    <Chip tone="neu" small>
      {T('Valgt av deg')}
    </Chip>
  ) : b ? (
    <Chip tone={BASIS[b][1]} small>
      {T(BASIS[b][0])}
    </Chip>
  ) : (
    <Chip tone="neu" small>
      {T('Ingen treff')}
    </Chip>
  );

export interface PayTypeSuggestion {
  code: string;
  name: string;
  calculation_type: 'percent' | 'extra_per_hour';
  rate_value: number;
  /** #183: Go's item pays base and supplement (percent over 100); made as a type that pays the whole hour */
  pays_whole_hour?: boolean;
  percent?: number;
}
/**
 * PowerOffice's supplement and overtime pay types that no Timefly pay type uses yet, made in Timefly
 * through the same POST /admin/payroll/types as Innstillinger (#183 phase 2). When a supplement
 * applies (evening, weekend) is set afterwards: PowerOffice does not know it.
 */
function PayTypesFromGo({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['poweroffice-pay-types'],
    queryFn: () => get<{ suggestions: PayTypeSuggestion[] }>('/admin/poweroffice/pay-types'),
  });
  const [pick, setPick] = useState<Record<string, number>>({});
  const make = useMutation({
    mutationFn: async () => {
      const list = q.data?.suggestions ?? [];
      let n = 0;
      for (const sug of list.filter((x) => pick[x.code] !== undefined)) {
        await post('/admin/payroll/types', {
          name: sug.name,
          code: sug.code,
          calculation_type: sug.calculation_type,
          rate_value: pick[sug.code],
          ...(sug.pays_whole_hour ? { pays_whole_hour: true } : {}),
        });
        n++;
      }
      return n;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ['payroll-types'] });
      setPick({});
      q.refetch();
      toast(T('{n} lønnsarter opprettet. Sett når tilleggene gjelder under Innstillinger, Lønn.', { n }));
      onDone();
    },
    onError: (e) => toast(errorText(e)),
  });
  const list = q.data?.suggestions ?? [];
  if (q.isPending || !list.length) return null;
  const n = Object.keys(pick).length;
  // a kroner supplement at 0 kr would send its hours unpaid: it needs its rate first (owner 2026-10-19)
  const zero = list.filter(
    (x) =>
      pick[x.code] !== undefined && x.calculation_type === 'extra_per_hour' && !((pick[x.code] ?? 0) > 0),
  );
  return (
    <div data-testid="po-pay-types" style={{ display: 'grid', gap: 8 }}>
      <b style={{ fontSize: 14 }}>
        {T('Tillegg og overtid i PowerOffice som ikke finnes i Timefly ({n})', { n: list.length })}
      </b>
      <span style={{ color: 'var(--ink3)', fontSize: 13 }}>
        {T(
          'En lønnsart over 100 % betaler grunnlønn og tillegg sammen. Den opprettes som en lønnsart som betaler hele timen: timene flyttes fra Vanlige timer, og satsen her er tillegget.',
        )}
      </span>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
        {list.map((x) => (
          <li key={x.code}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="checkbox"
                checked={pick[x.code] !== undefined}
                onChange={(e) => {
                  const next = { ...pick };
                  if (e.target.checked) next[x.code] = x.rate_value;
                  else delete next[x.code];
                  setPick(next);
                }}
              />
              <span>
                {x.code}, {x.name}
                {x.pays_whole_hour ? ` (${T('betaler hele timen, {p} %', { p: x.percent ?? '' })})` : ''}
              </span>
              {pick[x.code] !== undefined && (
                <input
                  className="inp"
                  style={{ width: 90 }}
                  inputMode="decimal"
                  aria-label={T('Sats for {name}', { name: x.name })}
                  value={String(pick[x.code])}
                  onChange={(e) =>
                    setPick({ ...pick, [x.code]: Number(e.target.value.replace(',', '.')) || 0 })
                  }
                />
              )}
              <small style={{ color: 'var(--ink3)' }}>
                {x.pays_whole_hour
                  ? T('% tillegg')
                  : x.calculation_type === 'percent'
                    ? T('% av timelønn')
                    : T('kr per time')}
              </small>
            </label>
          </li>
        ))}
      </ul>
      <span>
        <Button onClick={() => make.mutate()} disabled={make.isPending || n === 0 || zero.length > 0}>
          {make.isPending ? T('Oppretter…') : T('Opprett {n} lønnsarter', { n })}
        </Button>
        {zero.length > 0 && (
          <small style={{ display: 'block', color: 'var(--dgr-i)' }} data-testid="po-pay-types-zero">
            {T(
              'Skriv inn satsen i kroner per time for {names}. PowerOffice har ingen sats på disse, og 0 kr betaler ingenting.',
              {
                names: zero.map((x) => x.name).join(', '),
              },
            )}
          </small>
        )}
      </span>
    </div>
  );
}

export function PowerOfficeMatchPanel({
  onClose,
  sys = POWEROFFICE,
}: { onClose: () => void; sys?: PayrollSystem }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: [sys.key, 'match'],
    queryFn: () => get<PowerOfficeMatch>(`${sys.base}/match`),
    staleTime: 0,
  });
  const [people, setPeople] = useState<Record<number, string>>({});
  const [types, setTypes] = useState<Record<string, string>>({});
  // which Timefly types pay the whole hour, so an item over 100 % is not warned about for them (#183)
  const payTypes = usePayrollTypes();
  // the suggestions are the starting choice; what the person picks stays until they save or close
  useEffect(() => {
    if (!q.data) return;
    setPeople(Object.fromEntries(q.data.people.map((p) => [p.user_id, p.suggestion ?? p.current ?? ''])));
    setTypes(Object.fromEntries(q.data.types.map((t) => [String(t.key), t.suggestion ?? t.current ?? ''])));
  }, [q.data]);
  const d = q.data;
  // one Go employee for one person, said before the server would refuse it
  const twice = useMemo(() => {
    const seen = new Map<string, number>();
    for (const v of Object.values(people)) if (v) seen.set(v, (seen.get(v) ?? 0) + 1);
    return new Set([...seen].filter(([, n]) => n > 1).map(([k]) => k));
  }, [people]);
  const changed = d
    ? {
        people: d.people
          .filter((p) => (people[p.user_id] ?? '') !== (p.current ?? ''))
          .map((p) => ({ user_id: p.user_id, number: people[p.user_id] || null })),
        types: d.types
          .filter((t) => (types[String(t.key)] ?? '') !== (t.current ?? ''))
          .map((t) => ({ key: t.key, code: types[String(t.key)] || null })),
      }
    : { people: [], types: [] };
  // a company of several stores on one Go company sets its lønnsarter once (#183)
  const [allStores, setAllStores] = useState(true);
  const save = useMutation({
    mutationFn: () =>
      put<PowerOfficeMatch>(`${sys.base}/match`, {
        ...changed,
        ...(d?.shared_stores?.length ? { all_stores: allStores } : {}),
      }),
    onSuccess: (r) => {
      qc.setQueryData([sys.key, 'match'], r);
      toast(T('Koblingene er lagret. Lønns-ID og lønnsarter er oppdatert.'));
    },
    onError: (e) => toast(errorText(e)),
  });
  const count = (sure: boolean | null) =>
    d ? d.people.filter((p) => (sure == null ? !p.suggestion : !!p.suggestion && p.sure === sure)).length : 0;
  const n = changed.people.length + changed.types.length;
  return (
    <section data-testid="po-match" style={{ display: 'grid', gap: 14, marginTop: 6 }}>
      <div>
        <b style={{ fontSize: 15 }}>{T('Koble ansatte og lønnsarter')}</b>
        <p style={{ margin: '4px 0 0', color: 'var(--ink3)', fontSize: 13 }}>
          {T(
            'Forslagene kommer fra {system} og bygger på e-post, telefon, navn og fødselsdato. Se over, rett det som er feil og trykk Lagre. Ingenting endres før du lagrer.',
            // the panel serves both systems (#187); it named PowerOffice Go inside Tripletex (#191)
            { system: sys.key === 'tripletex' ? 'Tripletex' : 'PowerOffice Go' },
          )}
        </p>
      </div>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>
          {T('Henter ansatte og lønnsarter fra {system} …', { system: sys.short })}
        </output>
      ) : q.isError || !d ? (
        <Notice tone="dgr" title={T('Kunne ikke hente fra {system}', { system: sys.short })}>
          {errorText(q.error)}{' '}
          <Button small onClick={() => q.refetch()}>
            {T('Prøv igjen')}
          </Button>
        </Notice>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 13 }} data-testid="po-match-summary">
            {T('{sure} sikre forslag, {check} å sjekke, {none} uten treff', {
              sure: count(true),
              check: count(false),
              none: count(null),
            })}
          </p>
          <table className="t" data-testid="po-match-people">
            <thead>
              <tr>
                <th>{T('Ansatt i Timefly')}</th>
                <th>{T('Ansatt i {system}', { system: sys.short })}</th>
                <th>{T('Grunnlag')}</th>
              </tr>
            </thead>
            <tbody>
              {d.people.map((p) => {
                const v = people[p.user_id] ?? '';
                return (
                  <tr key={p.user_id}>
                    <td>{p.name}</td>
                    <td>
                      <select
                        className="inp"
                        aria-label={T('Ansatt i {system} for {name}', { name: p.name, system: sys.short })}
                        value={v}
                        aria-invalid={twice.has(v) || undefined}
                        onChange={(e) => setPeople({ ...people, [p.user_id]: e.target.value })}
                      >
                        <option value="">{T('Ingen')}</option>
                        {/* a Lønns-ID Go does not have is shown as it is, never dropped unseen */}
                        {v && !d.employees.some((e) => e.number === v) && (
                          <option value={v}>
                            {T('{n}, finnes ikke i {system}', { n: v, system: sys.short })}
                          </option>
                        )}
                        {d.employees.map((e) => (
                          <option key={e.number} value={e.number}>
                            {e.number}, {e.name}
                          </option>
                        ))}
                      </select>
                      {twice.has(v) && (
                        <small style={{ color: 'var(--dgr-i)' }}>{T('Valgt for flere personer')}</small>
                      )}
                    </td>
                    <td>{why(p.basis, v, p)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!!d.missing?.length && <AddFromGo missing={d.missing} onDone={() => q.refetch()} sys={sys} />}
          {d.department_needed && (
            <span style={{ color: 'var(--ink3)', fontSize: 13 }}>{T(DEPT_NEEDED)}</span>
          )}
          {/* Tripletex's salary types carry no rate, so its supplements are not suggested here (#187) */}
          {sys.key === 'poweroffice' && <PayTypesFromGo onDone={() => q.refetch()} />}
          <table className="t" data-testid="po-match-types">
            <thead>
              <tr>
                <th>{T('Lønnsart i Timefly')}</th>
                <th>{T('Lønnsart i {system}', { system: sys.short })}</th>
                <th>{T('Grunnlag')}</th>
              </tr>
            </thead>
            <tbody>
              {d.types.map((t) => {
                const v = types[String(t.key)] ?? '';
                return (
                  <tr key={String(t.key)}>
                    <td>{t.key === 'regular' ? T('Vanlige timer') : t.name}</td>
                    <td>
                      <select
                        className="inp"
                        aria-label={T('Lønnsart i {system} for {name}', { name: t.name, system: sys.short })}
                        value={v}
                        onChange={(e) => setTypes({ ...types, [String(t.key)]: e.target.value })}
                      >
                        <option value="">{T('Ingen')}</option>
                        {v && !d.pay_items.some((i) => i.code === v) && (
                          <option value={v}>
                            {T('{n}, finnes ikke i {system}', { n: v, system: sys.short })}
                          </option>
                        )}
                        {d.pay_items.map((i) => (
                          <option key={i.code} value={i.code}>
                            {i.code}, {i.name}
                          </option>
                        ))}
                      </select>
                      {(() => {
                        const item = d.pay_items.find((x) => x.code === v);
                        const whole = (Array.isArray(payTypes.data) ? payTypes.data : []).some(
                          (p) => String(p.id) === String(t.key) && p.paysWholeHour,
                        );
                        return item?.percent && item.percent > 100 && t.key !== 'regular' && !whole ? (
                          <small
                            style={{ display: 'block', color: 'var(--dgr-i)' }}
                            data-testid="po-base-twice"
                          >
                            {T(
                              'Denne lønnsarten betaler grunnlønn og tillegg sammen ({p} %). Kryss av «Lønnsarten betaler hele timen» på lønnsarten under Innstillinger, Lønn, ellers betales grunnlønnen to ganger. Eller velg lønnsarten for bare tillegget.',
                              { p: item.percent },
                            )}
                          </small>
                        ) : null;
                      })()}
                    </td>
                    <td>{why(t.basis, v, t)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!!d.shared_stores?.length && (
            <label
              style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}
              data-testid="po-all-stores"
            >
              <input type="checkbox" checked={allStores} onChange={(e) => setAllStores(e.target.checked)} />
              {T('Bruk de samme lønnsartene i alle butikkene ({names})', {
                names: d.shared_stores.map((x) => x.name).join(', '),
              })}
            </label>
          )}
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              tone="pri"
              onClick={() => save.mutate()}
              disabled={save.isPending || twice.size > 0 || n === 0}
            >
              {save.isPending ? T('Lagrer…') : n ? T('Lagre {n} endringer', { n }) : T('Ingen endringer')}
            </Button>
            <Button onClick={onClose} disabled={save.isPending}>
              {T('Lukk')}
            </Button>
          </span>
        </>
      )}
    </section>
  );
}
